import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Pencil, Check, X, RefreshCw, Trash2, FolderOpen, Calendar, Hash, FileText, Layers, GripVertical, LoaderCircle, ChevronUp, ChevronDown, ChevronRight, Info, Filter, TriangleAlert, TextAlignStart, Ban, Eye, RotateCcw, Sparkles } from 'lucide-react';
import { api } from '@/lib/api';
import { friendlyStorageError } from '@/lib/storageErrorCopy';
import { useProject } from '@/lib/ProjectContext';
import { usePermissions } from '@/lib/usePermissions';
import { Telemetry } from '@/lib/telemetry';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { AutoLinkText } from '@/components/shared/AutoLinkText';
import { FolderTagInput } from '@/components/shared/FolderTagInput';
import { useScanTask } from '@/lib/useScanTask';
import { ScanProgress } from '@/components/shared/ScanProgress';
import type { Submission, IngestResult, RescanResult, FolderFilterState } from '@/types/api';

const DESCRIPTION_MAX_LEN = 2000;

export function ManageProjectPage() {
  const navigate = useNavigate();
  const { activeProject, projects, setActiveProject, reload } = useProject();
  const { canDeleteProjects, hasRole } = usePermissions();
  const canEditProject = hasRole('lead');
  const canContribute = hasRole('contributor');
  const [editingField, setEditingField] = useState<'name' | 'ind_number' | 'description' | 'root_path' | null>(null);
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deletingListId, setDeletingListId] = useState<string | null>(null);
  const [confirmDeleteListId, setConfirmDeleteListId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Advanced settings — folder rules (A2F-477 ignore + A2F-656 C3 active)
  const [rulesExpanded, setRulesExpanded] = useState(false);
  const [ignoreFolders, setIgnoreFolders] = useState<string[]>([]);
  const [activeFolders, setActiveFolders] = useState<string[]>([]);
  const [savingRules, setSavingRules] = useState(false);
  const [rulesSavedNudge, setRulesSavedNudge] = useState(false);

  // A2F-1035 — folder-filter legibility: the locked system core, the seeded
  // marker, and the convention defaults (for the "Add recommended" proposal).
  // Loaded from GET /folder-filters alongside the project.
  const [folderFilters, setFolderFilters] = useState<FolderFilterState | null>(null);
  const [addingRecommended, setAddingRecommended] = useState(false);
  // The proposal is a gentle nudge, not a nag — let the user dismiss it for
  // this session without accepting.
  const [proposalDismissed, setProposalDismissed] = useState(false);

  // Submissions list + reorder
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [loadingSubs, setLoadingSubs] = useState(false);
  // A2F review fix — distinguish "loaded, genuinely empty" from "load failed".
  // Without this, a 401/403/500/timeout on the primary submissions fetch
  // collapsed to a false "No submissions found." with no error surfaced.
  const [subsLoadError, setSubsLoadError] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);
  const [orderDirty, setOrderDirty] = useState(false);
  const dragIdx = useRef<number | null>(null);
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null);

  // A2F-1024 — lifecycle: show-hidden toggle + per-row disable in-flight.
  // Submissions are only ever soft-deleted (ingest tombstone) or disabled
  // (user); there is no hard-delete affordance.
  const [showHidden, setShowHidden] = useState(false);
  const [togglingDisableSubId, setTogglingDisableSubId] = useState<string | null>(null);

  useEffect(() => {
    if (!activeProject) return;
    setLoadingSubs(true);
    setSubsLoadError(false);
    // A2F-1024: always fetch the COMPLETE set (include_completed + include_hidden).
    // Reorder requires every submission (the backend enforces "all submissions
    // exactly once"), so `submissions` is always the full, newest-first list.
    // `showHidden` is a display-only filter (see `visibleSubmissions`) and so is
    // intentionally NOT in this effect's dep array — toggling it never refetches.
    api.projects.submissions(activeProject.id, true, true)
      .then((subs) => {
        // Display newest first (reverse of sequence_number order)
        setSubmissions([...subs].reverse());
        setOrderDirty(false);
      })
      .catch((e: any) => {
        // Surface the failure via the page banner instead of silently showing
        // "No submissions found." Clear any stale list so the error state isn't
        // contradicted by leftover rows.
        setSubmissions([]);
        setSubsLoadError(true);
        setError(e?.message || 'Failed to load submissions');
      })
      .finally(() => setLoadingSubs(false));
  }, [activeProject?.id]);

  // Sync folder-rules form state when the active project changes.
  // Intentionally excludes `activeProject.folderRules` from the dep array:
  // reload() returns a fresh object after every fetch (convertKeys rebuilds
  // the tree), so depending on it would re-run this effect on every save
  // and clobber rulesSavedNudge before the user ever sees it.
  useEffect(() => {
    if (!activeProject) return;
    setIgnoreFolders(activeProject.folderRules?.ignoreFolderNames || []);
    setActiveFolders(activeProject.folderRules?.activeFolderNames || []);
    setRulesSavedNudge(false);
    setProposalDismissed(false);
  }, [activeProject?.id]);

  // A2F-1035 — load the folder-filter state (system core, seeded marker,
  // convention defaults, effective set). Re-fetched on project switch and
  // after every rules save via the `rulesReloadKey` bump so the seeded marker
  // and effective set stay in sync with what the user just persisted.
  const [rulesReloadKey, setRulesReloadKey] = useState(0);
  useEffect(() => {
    if (!activeProject) return;
    let cancelled = false;
    api.projects.folderFilters(activeProject.id)
      .then((ff) => { if (!cancelled) setFolderFilters(ff); })
      .catch(() => { if (!cancelled) setFolderFilters(null); });
    return () => { cancelled = true; };
  }, [activeProject?.id, rulesReloadKey]);

  const rulesDirty = !!activeProject && (
    JSON.stringify(ignoreFolders) !== JSON.stringify(activeProject.folderRules?.ignoreFolderNames || [])
    || JSON.stringify(activeFolders) !== JSON.stringify(activeProject.folderRules?.activeFolderNames || [])
  );

  // A2F-1035 — show the "Add recommended" proposal only for un-seeded projects
  // (the convention defaults are applied as a hidden fallback, not yet visible
  // chips), and only until the user dismisses it for this session.
  const showProposal = !!folderFilters
    && !folderFilters.ignoreSeeded
    && folderFilters.defaultIgnoreGlobs.length > 0
    && !proposalDismissed;

  const handleSaveRules = useCallback(async () => {
    if (!activeProject) return;
    setSavingRules(true);
    setError(null);
    try {
      await api.projects.update(activeProject.id, {
        folder_rules: {
          ignore_folder_names: ignoreFolders,
          active_folder_names: activeFolders,
        },
      });
      await reload();
      setRulesReloadKey((k) => k + 1);
      setRulesSavedNudge(true);
      Telemetry.projectFolderRulesConfigured({
        ignore_count: ignoreFolders.length,
        active_count: activeFolders.length,
        surface: 'manage_project',
      });
    } catch (e: any) {
      setError(e.message || 'Failed to save folder rules');
    } finally {
      setSavingRules(false);
    }
  }, [activeProject, ignoreFolders, activeFolders, reload]);

  // A2F-1024 — a row is hidden when the user disabled it (sticky intent) or
  // ingest tombstoned it (deleted_at; source folder gone). Single source of
  // truth, reused by `visibleSubmissions`, the merge below, and the row render.
  const isHidden = (s: Submission) => s.isDisabled || !!s.deletedAt;

  // What the list actually renders. `submissions` is always the COMPLETE set
  // (newest-first); Show-hidden is a pure display filter. When it's off, the
  // visible rows are a contiguous, freely-reorderable subset.
  const visibleSubmissions = showHidden ? submissions : submissions.filter((s) => !isHidden(s));

  // Reconstruct the full ordered list from a reordering of just the visible
  // rows. Stable merge: walk the original full order; each slot that *was*
  // visible takes the next visible row in its new order, each hidden slot keeps
  // its own row. Hidden rows therefore stay in their original relative
  // positions, and the result is always the complete set — so the backend's
  // "all submissions exactly once" contract holds in either toggle state.
  const mergeVisibleOrder = (full: Submission[], newVisibleOrder: Submission[]): Submission[] => {
    let v = 0;
    return full.map((s) => (isHidden(s) ? s : newVisibleOrder[v++]));
  };

  // A2F-1035 — accept the proposed convention defaults. Unions
  // defaultIgnoreGlobs into the current ignore list (case-insensitive dedupe,
  // matching the backend's seeded_default_rules materializer) and PATCHes
  // folder_rules with ignore_seeded: true. ignore_seeded is sticky-on-true
  // server-side, so this is a one-way "stop applying these as a hidden
  // fallback, make them visible editable chips" action.
  const handleAddRecommended = useCallback(async () => {
    if (!activeProject || !folderFilters) return;
    setAddingRecommended(true);
    setError(null);
    const existing = new Set(ignoreFolders.map((v) => v.toLowerCase()));
    const merged = [...ignoreFolders];
    for (const g of folderFilters.defaultIgnoreGlobs) {
      if (!existing.has(g.toLowerCase())) {
        merged.push(g);
        existing.add(g.toLowerCase());
      }
    }
    try {
      await api.projects.update(activeProject.id, {
        folder_rules: {
          ignore_folder_names: merged,
          active_folder_names: activeFolders,
          ignore_seeded: true,
        },
      });
      await reload();
      setIgnoreFolders(merged);
      // Optimistically flip the local marker so the proposal disappears
      // immediately on accept; the rulesReloadKey re-fetch confirms it.
      setFolderFilters((prev) => (prev ? { ...prev, ignoreSeeded: true, ignoreFolderNames: merged } : prev));
      setProposalDismissed(true);
      setRulesReloadKey((k) => k + 1);
      setRulesSavedNudge(true);
      Telemetry.projectFolderRulesConfigured({
        ignore_count: merged.length,
        active_count: activeFolders.length,
        surface: 'manage_project',
      });
    } catch (e: any) {
      setError(e.message || 'Failed to add recommended folders');
    } finally {
      setAddingRecommended(false);
    }
  }, [activeProject, folderFilters, ignoreFolders, activeFolders, reload]);

  const handleSubDragStart = (idx: number) => { dragIdx.current = idx; };
  const handleSubDragOver = (e: React.DragEvent, idx: number) => { e.preventDefault(); setDragOverIdx(idx); };
  // NB: idx values are positions in `visibleSubmissions`, the rendered list.
  const handleSubDrop = (idx: number) => {
    const from = dragIdx.current;
    if (from === null || from === idx) { dragIdx.current = null; setDragOverIdx(null); return; }
    const nextVisible = [...visibleSubmissions];
    const [moved] = nextVisible.splice(from, 1);
    nextVisible.splice(idx, 0, moved);
    setSubmissions((prev) => mergeVisibleOrder(prev, nextVisible));
    setOrderDirty(true);
    dragIdx.current = null;
    setDragOverIdx(null);
  };
  const handleSubDragEnd = () => { dragIdx.current = null; setDragOverIdx(null); };

  const handleMoveSub = (idx: number, direction: 'up' | 'down') => {
    const target = direction === 'up' ? idx - 1 : idx + 1;
    if (target < 0 || target >= visibleSubmissions.length) return;
    const nextVisible = [...visibleSubmissions];
    [nextVisible[idx], nextVisible[target]] = [nextVisible[target], nextVisible[idx]];
    setSubmissions((prev) => mergeVisibleOrder(prev, nextVisible));
    setOrderDirty(true);
  };

  const handleSaveOrder = useCallback(async () => {
    if (!activeProject) return;
    setSavingOrder(true);
    setError(null);
    try {
      // `submissions` is always the complete set; reverse back to oldest-first
      // for the backend, which requires every submission exactly once (A2F-1024).
      const ids = [...submissions].reverse().map((s) => s.id);
      await api.projects.reorderSubmissions(activeProject.id, ids);
      setOrderDirty(false);
      await reload();
    } catch (e: any) {
      setError(e?.message || 'Failed to save order');
    } finally {
      setSavingOrder(false);
    }
  }, [activeProject, submissions, reload]);

  if (!activeProject) return <LoadingSpinner message="Loading project..." />;

  const startEdit = (field: 'name' | 'ind_number' | 'description' | 'root_path') => {
    setEditingField(field);
    setEditValue(
      field === 'name' ? activeProject.name
        : field === 'ind_number' ? activeProject.indNumber
        : field === 'description' ? (activeProject.description ?? '')
        : activeProject.rootPath
    );
    setError(null);
  };

  const cancelEdit = () => {
    setEditingField(null);
    setEditValue('');
    setError(null);
  };

  const saveEdit = async () => {
    if (!editingField) return;
    // Description is the only editable field that's allowed to be empty
    // (clears the description). Name / IND / root_path still require a value.
    if (editingField !== 'description' && !editValue.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const trimmed = editValue.trim();
      const body = editingField === 'name'
        ? { name: trimmed }
        : editingField === 'ind_number'
        ? { ind_number: trimmed }
        : editingField === 'description'
        ? { description: trimmed }
        : { root_path: trimmed };
      await api.projects.update(activeProject.id, body);
      await reload();
      setEditingField(null);
    } catch (e: any) {
      setError(e.message || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  // Async ingest task (A2F-504; A2F-771 moved this onto a durable queue
  // so a stale updatedAt is no longer a "task is dead" signal — the
  // worker's heartbeat keeps the row fresh while it's actually running,
  // and queued rows can sit untouched for minutes between user POST and
  // worker pickup. The resume-on-mount path below now blindly latches
  // onto whatever's queued or running.)

  const {
    taskState: ingestTaskState,
    startTask: startIngestTask,
    cancel: cancelIngest,
    reset: resetIngest,
  } = useScanTask<IngestResult>(
    undefined,
    () => {
      // onComplete — refresh project data. Button + dialog visibility
      // are derived from ingestTaskState.isScanning / .error, so the
      // cancel and failure paths self-clean without needing a flag here.
      reload();
    },
  );

  // A2F-757 — per-submission rescan
  const [rescanningSubId, setRescanningSubId] = useState<string | null>(null);
  const { taskState: rescanTaskState, startTask: startRescanTask, cancel: cancelRescan } = useScanTask<RescanResult>(
    undefined,
    () => {
      // onComplete — refresh submissions list so document counts update.
      // Always fetch the complete set (include_hidden=true) so reorder keeps
      // working; `visibleSubmissions` applies the Show-hidden filter (A2F-1024).
      if (activeProject) {
        api.projects.submissions(activeProject.id, true, true)
          .then((subs) => setSubmissions([...subs].reverse()))
          // Background refresh after a rescan — the list already rendered, so a
          // failure here is non-fatal. Warn instead of surfacing a banner.
          .catch((e: any) => console.warn('Submissions refresh after rescan failed:', e));
      }
      setRescanningSubId(null);
    },
    // onFailed: deliberately do NOT clear rescanningSubId here. The
    // error panel below is gated on ``rescanningSubId && (isScanning
    // || error)``, so clearing the id on failure would unmount the
    // panel before the user sees the error — they'd get no retry
    // path or feedback. The error UI's Dismiss button
    // (``onCancel={rescanTaskState.error ? () => setRescanningSubId(null) : cancelRescan}``)
    // is how the user clears it manually.
  );

  const handleRescanSubmission = useCallback(async (submissionId: string) => {
    if (!activeProject) return;
    setError(null);
    setRescanningSubId(submissionId);
    try {
      const { taskId } = await api.projects.rescanSubmission(activeProject.id, submissionId);
      startRescanTask(taskId);
    } catch (e) {
      setError(friendlyStorageError(e, 'Rescan failed.'));
      setRescanningSubId(null);
    }
  }, [activeProject, startRescanTask]);

  // Resume-on-mount: check the durable queue for a queued or running
  // ingest job for this project. A2F-771: task_type is the job's
  // ``kind`` value (``ingest_full``), not the legacy ``ingest``. The
  // backend's GET /tasks/running endpoint queries the jobs table when
  // the kind matches a durable queue value.
  useEffect(() => {
    if (!activeProject) return;
    let cancelled = false;
    api.tasks.findRunning(activeProject.id, 'ingest_full')
      .then((task) => {
        if (cancelled) return;
        startIngestTask(task.taskId);
      })
      .catch(() => {
        // No running task — expected normal case
      });
    return () => { cancelled = true; };
  }, [activeProject?.id, startIngestTask]);

  const handleReIngest = async () => {
    if (!activeProject) return;
    setError(null);
    try {
      const { taskId } = await api.projects.ingest(activeProject.id);
      startIngestTask(taskId);
    } catch (e) {
      setError(friendlyStorageError(e, 'Re-ingest failed.'));
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    setError(null);
    try {
      await api.projects.delete(activeProject.id);
      Telemetry.projectDeleted();
      localStorage.removeItem('chrona_active_project');
      await reload();
      navigate('/');
    } catch (e: any) {
      setError(e.message || 'Delete failed');
      setDeleting(false);
    }
  };

  // A2F-1024 — the only per-row lifecycle action. Toggling is_disabled is the
  // soft, reversible "keep this folder out" (submissions are never hard-deleted;
  // they're disabled by the user or tombstoned by ingest). The endpoint returns
  // the refreshed SubmissionOut; we always patch it in place so `submissions`
  // stays the complete set (reorder depends on that). `visibleSubmissions`
  // then hides a freshly disabled row when Show-hidden is OFF, or keeps it with
  // a flipped badge when ON — no manual array surgery, no stale-array risk.
  const handleToggleDisable = async (submissionId: string, disabled: boolean) => {
    if (!activeProject) return;
    setTogglingDisableSubId(submissionId);
    setError(null);
    try {
      const updated = await api.projects.disableSubmission(activeProject.id, submissionId, disabled);
      setSubmissions((prev) => prev.map((s) => (s.id === submissionId ? updated : s)));
      await reload();
    } catch (e: any) {
      setError(e.message || (disabled ? 'Disable submission failed' : 'Enable submission failed'));
    } finally {
      setTogglingDisableSubId(null);
    }
  };

  const handleDeleteFromList = async (projectId: string) => {
    setDeletingListId(projectId);
    setError(null);
    try {
      await api.projects.delete(projectId);
      if (activeProject.id === projectId) {
        localStorage.removeItem('chrona_active_project');
      }
      await reload();
      setConfirmDeleteListId(null);
      setDeletingListId(null);
      // If we deleted the active project, navigate away
      if (activeProject.id === projectId) {
        navigate('/');
      }
    } catch (e: any) {
      setError(e.message || 'Delete failed');
      setDeletingListId(null);
    }
  };

  const createdDate = activeProject.createdAt
    ? new Date(activeProject.createdAt).toLocaleDateString('en-US', {
        year: 'numeric', month: 'long', day: 'numeric',
      })
    : 'Unknown';

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <p className="text-[10px] font-semibold text-brand-orange uppercase tracking-widest">
          Settings
        </p>
        <h1 className="text-xl font-bold text-brand-charcoal mt-1">Manage Project</h1>
      </div>

      {/* Error Banner */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Project Details Card */}
      <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-5">
        {/* Name */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <FileText className="w-4 h-4 text-slate-400" />
            <span className="text-sm text-slate-500">Project Name</span>
          </div>
          {editingField === 'name' ? (
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                className="text-sm border border-slate-200 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-brand-orange/30 focus:border-brand-orange"
                autoFocus
                onKeyDown={(e) => e.key === 'Enter' && saveEdit()}
              />
              <button onClick={saveEdit} disabled={saving} className="p-1 text-emerald-600 hover:bg-emerald-50 rounded">
                <Check className="w-4 h-4" />
              </button>
              <button onClick={cancelEdit} className="p-1 text-slate-400 hover:bg-slate-50 rounded">
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-brand-charcoal">{activeProject.name}</span>
              <button
                onClick={() => startEdit('name')}
                disabled={!canEditProject}
                title={!canEditProject ? 'Requires Lead role' : undefined}
                className={`p-1 text-slate-400 hover:text-brand-orange hover:bg-orange-50 rounded${!canEditProject ? ' opacity-50 cursor-not-allowed' : ''}`}
              >
                <Pencil className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
        </div>

        <div className="border-t border-slate-100" />

        {/* IND Number */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Hash className="w-4 h-4 text-slate-400" />
            <span className="text-sm text-slate-500">IND Number</span>
          </div>
          {editingField === 'ind_number' ? (
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                className="text-sm border border-slate-200 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-brand-orange/30 focus:border-brand-orange"
                autoFocus
                onKeyDown={(e) => e.key === 'Enter' && saveEdit()}
              />
              <button onClick={saveEdit} disabled={saving} className="p-1 text-emerald-600 hover:bg-emerald-50 rounded">
                <Check className="w-4 h-4" />
              </button>
              <button onClick={cancelEdit} className="p-1 text-slate-400 hover:bg-slate-50 rounded">
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-brand-charcoal">{activeProject.indNumber}</span>
              <button
                onClick={() => startEdit('ind_number')}
                disabled={!canEditProject}
                title={!canEditProject ? 'Requires Lead role' : undefined}
                className={`p-1 text-slate-400 hover:text-brand-orange hover:bg-orange-50 rounded${!canEditProject ? ' opacity-50 cursor-not-allowed' : ''}`}
              >
                <Pencil className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
        </div>

        <div className="border-t border-slate-100" />

        {/* Description (A2F-733) — stacked layout because it's multi-line */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <TextAlignStart className="w-4 h-4 text-slate-400" />
              <span className="text-sm text-slate-500">Description</span>
            </div>
            {editingField !== 'description' && (
              <button
                onClick={() => startEdit('description')}
                disabled={!canEditProject}
                title={!canEditProject ? 'Requires Lead role' : undefined}
                className={`p-1 text-slate-400 hover:text-brand-orange hover:bg-orange-50 rounded${!canEditProject ? ' opacity-50 cursor-not-allowed' : ''}`}
              >
                <Pencil className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          {editingField === 'description' ? (
            <div className="space-y-2">
              <textarea
                value={editValue}
                onChange={(e) => setEditValue(e.target.value.slice(0, DESCRIPTION_MAX_LEN))}
                rows={4}
                maxLength={DESCRIPTION_MAX_LEN}
                placeholder="Optional notes about this program. URLs become clickable links."
                className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-orange/30 focus:border-brand-orange"
                autoFocus
              />
              <div className="flex items-center justify-between text-xs">
                <span className="text-slate-400">
                  {editValue.length} / {DESCRIPTION_MAX_LEN}
                </span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={cancelEdit}
                    className="text-slate-500 hover:text-brand-charcoal px-3 py-1 rounded"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={saveEdit}
                    disabled={saving}
                    className="bg-brand-orange text-white px-3 py-1 rounded hover:bg-brand-orange/90 disabled:opacity-50"
                  >
                    {saving ? 'Saving…' : 'Save'}
                  </button>
                </div>
              </div>
            </div>
          ) : activeProject.description ? (
            <AutoLinkText
              text={activeProject.description}
              className="block text-sm text-slate-700 whitespace-pre-wrap"
            />
          ) : (
            <span className="block text-xs italic text-slate-400">
              No description.
            </span>
          )}
        </div>

        <div className="border-t border-slate-100" />

        {/* Root Path */}
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 shrink-0">
            <FolderOpen className="w-4 h-4 text-slate-400" />
            <span className="text-sm text-slate-500">Root Path</span>
          </div>
          {editingField === 'root_path' ? (
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <input
                type="text"
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                className="flex-1 text-sm font-mono border border-slate-200 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-brand-orange/30 focus:border-brand-orange"
                autoFocus
                onKeyDown={(e) => e.key === 'Enter' && saveEdit()}
                placeholder="/path/to/IND/folder"
              />
              <button onClick={saveEdit} disabled={saving} className="p-1 text-emerald-600 hover:bg-emerald-50 rounded shrink-0" title="Save (will validate path)">
                {saving ? <LoaderCircle className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              </button>
              <button onClick={cancelEdit} className="p-1 text-slate-400 hover:bg-slate-50 rounded shrink-0">
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-sm text-slate-600 font-mono truncate" title={activeProject.rootPath}>
                {activeProject.rootPath}
              </span>
              <button
                onClick={() => startEdit('root_path')}
                disabled={!canEditProject}
                title={!canEditProject ? 'Requires Lead role' : 'Edit root path (document associations are preserved — use Re-Ingest to re-scan)'}
                className={`p-1 text-slate-400 hover:text-brand-orange hover:bg-orange-50 rounded shrink-0${!canEditProject ? ' opacity-50 cursor-not-allowed' : ''}`}
              >
                <Pencil className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
        </div>

        <div className="border-t border-slate-100" />

        {/* Created Date */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Calendar className="w-4 h-4 text-slate-400" />
            <span className="text-sm text-slate-500">Created</span>
          </div>
          <span className="text-sm text-slate-600">{createdDate}</span>
        </div>

        <div className="border-t border-slate-100" />

        {/* Stats Row */}
        <div className="flex items-center gap-8">
          <div className="flex items-center gap-3">
            <Layers className="w-4 h-4 text-slate-400" />
            <span className="text-sm text-slate-500">Submissions</span>
            <span className="text-sm font-semibold text-brand-charcoal">{activeProject.submissionCount}</span>
          </div>
          <div className="flex items-center gap-3">
            <FileText className="w-4 h-4 text-slate-400" />
            <span className="text-sm text-slate-500">Documents</span>
            <span className="text-sm font-semibold text-brand-charcoal">{activeProject.documentCount}</span>
          </div>
        </div>
      </div>

      {/* Advanced Settings (A2F-476 folder rules + A2F-495 file type) */}
      <div className="bg-white rounded-xl border border-slate-200">
        <button
          type="button"
          onClick={() => setRulesExpanded((v) => !v)}
          className="w-full flex items-center justify-between px-6 py-4 hover:bg-slate-50/40 transition-colors rounded-xl"
        >
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-brand-orange" />
            <h2 className="text-base font-semibold text-brand-charcoal">Advanced Settings</h2>
          </div>
          {rulesExpanded
            ? <ChevronDown className="w-4 h-4 text-slate-400" />
            : <ChevronRight className="w-4 h-4 text-slate-400" />}
        </button>

        {rulesExpanded && (
          <div className="px-6 pb-6 pt-4 space-y-6 border-t border-slate-100">
            {/* Ignore Folders — applied at ingestion (A2F-477 + A2F-1035 glob). */}
            <div>
              <p className="text-xs text-slate-500 leading-relaxed">
                Tell Chrona which folders to skip when scanning this project — useful for
                filtering out drafts, archives, or working copies that shouldn't be ingested.
                Leave empty to ingest every supported file (default).
              </p>

              <div className="mt-3">
                <FolderTagInput
                  label="Ignore Folders"
                  hint="Skip any folder matching one of these and everything inside it. Case-insensitive: a plain name matches exactly (Drafts); use * for the rest — Final* matches “Final Word Documents”, *PeC* matches anywhere."
                  values={ignoreFolders}
                  onChange={setIgnoreFolders}
                  disabled={!canEditProject || savingRules || addingRecommended}
                  placeholder="Drafts"
                  lockedValues={folderFilters?.systemCore}
                />
              </div>
              {folderFilters && folderFilters.systemCore.length > 0 && (
                <p className="text-[11px] text-slate-400 mt-1">
                  The muted chips are always ignored by Chrona (system files like dotfolders and
                  Office lock files) — they can't be removed.
                </p>
              )}

              {/* A2F-1035 — propose the convention defaults for un-seeded
                  (pre-A2F-1035) projects. New projects come pre-seeded, so this
                  only shows for older ones. Dismissible; not a modal. */}
              {showProposal && (
                <div
                  data-testid="seeded-proposal"
                  className="mt-3 flex items-start gap-2.5 px-3 py-2.5 rounded-lg border border-orange-200 bg-orange-50/60"
                >
                  <Sparkles className="w-4 h-4 text-brand-orange shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-brand-charcoal leading-relaxed">
                      Chrona can pre-fill recommended folders to ignore
                      {' '}
                      <span className="text-slate-500">
                        ({folderFilters!.defaultIgnoreGlobs.join(', ')})
                      </span>
                      . They're already applied behind the scenes — adding them makes them
                      visible, editable chips you can tune.
                    </p>
                    <div className="mt-2 flex items-center gap-2">
                      <button
                        data-testid="add-recommended"
                        onClick={handleAddRecommended}
                        disabled={!canEditProject || addingRecommended || savingRules}
                        title={!canEditProject ? 'Requires Lead role' : undefined}
                        className={`flex items-center gap-1.5 px-2.5 py-1 bg-brand-orange text-white text-[11px] font-semibold rounded-md hover:bg-orange-600 transition-colors disabled:opacity-40${!canEditProject ? ' cursor-not-allowed' : ''}`}
                      >
                        {addingRecommended ? <LoaderCircle className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                        {addingRecommended ? 'Adding…' : 'Add recommended'}
                      </button>
                      <button
                        onClick={() => setProposalDismissed(true)}
                        disabled={addingRecommended}
                        className="text-[11px] text-slate-500 hover:text-brand-charcoal px-2 py-1 rounded-md disabled:opacity-40"
                      >
                        Not now
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <p className="text-[11px] text-slate-500 mt-2">
                Changing these rules does not modify already-ingested documents. Hit Re-Ingest
                after saving to re-scan.
              </p>
            </div>

            {/* Working Folders — applied at document listing (A2F-656 C3 + A2F-1035 glob). */}
            <div className="border-t border-slate-100 pt-5">
              <p className="text-xs text-slate-500 leading-relaxed">
                Restrict this project to documents inside these folders (e.g. "Word Documents",
                "Final"). Affects every Chrona feature — See, Review, and Clear. Leave empty to
                include all folders (default). Opening a specific document by link still works
                regardless of this setting.
              </p>

              <div className="mt-3">
                <FolderTagInput
                  label="Working Folders"
                  hint="Show only documents inside folders matching one of these. Case-insensitive: a plain name matches exactly (Final); use * for the rest — Final* matches “Final Word Documents”, *Final* matches anywhere."
                  values={activeFolders}
                  onChange={setActiveFolders}
                  disabled={!canEditProject || savingRules || addingRecommended}
                  placeholder="Word Documents"
                />
              </div>
              <p className="text-[11px] text-slate-500 mt-1">
                Changing these rules does not invalidate cached scan results. Hit Rescan from
                the Review pages after saving to apply.
              </p>
            </div>

            <div className="flex items-center justify-between gap-3 border-t border-slate-100 pt-4">
              <div className="text-[11px] text-slate-500">
                {rulesSavedNudge && (
                  <span className="text-emerald-600 font-medium">Saved.</span>
                )}
              </div>
              <button
                onClick={handleSaveRules}
                disabled={!rulesDirty || savingRules || !canEditProject}
                title={!canEditProject ? 'Requires Lead role' : undefined}
                className={`flex items-center gap-1.5 px-3 py-1.5 bg-brand-orange text-white text-xs font-semibold rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-40 shrink-0${!canEditProject ? ' cursor-not-allowed' : ''}`}
              >
                {savingRules ? <LoaderCircle className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                {savingRules ? 'Saving...' : 'Save Rules'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Submissions Card */}
      <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Layers className="w-5 h-5 text-brand-orange" />
            <h2 className="text-base font-semibold text-brand-charcoal">Submissions</h2>
          </div>
          <div className="flex items-center gap-3">
            {/* A2F-1024 — reveal disabled + removed-from-source rows. Default
                view shows only active submissions. */}
            <label
              className="flex items-center gap-1.5 text-xs text-slate-500 cursor-pointer select-none"
              title="Show submissions you've disabled, plus folders ingest no longer sees (removed from source)."
            >
              <input
                type="checkbox"
                checked={showHidden}
                onChange={(e) => setShowHidden(e.target.checked)}
                className="rounded border-slate-300 text-brand-orange focus:ring-brand-orange/30"
              />
              <Eye className="w-3.5 h-3.5" />
              Show hidden
            </label>
            {orderDirty && (
              <button
                onClick={handleSaveOrder}
                disabled={savingOrder || !canContribute}
                title={!canContribute ? 'Requires Contributor role' : undefined}
                className={`flex items-center gap-1.5 px-3 py-1.5 bg-brand-orange text-white text-xs font-semibold rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-50${!canContribute ? ' cursor-not-allowed' : ''}`}
              >
                {savingOrder ? <LoaderCircle className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                {savingOrder ? 'Saving...' : 'Save Order'}
              </button>
            )}
          </div>
        </div>

        {loadingSubs && (
          <div className="flex items-center justify-center py-6 text-sm text-slate-400 gap-2">
            <LoaderCircle className="w-4 h-4 animate-spin" />
            Loading submissions...
          </div>
        )}

        {/* Distinguish a real failure from a genuinely empty project: the error
            banner above carries the message; here we just avoid the misleading
            "No submissions found." empty-state when the load actually failed. */}
        {!loadingSubs && subsLoadError && (
          <p className="text-sm text-red-600 text-center py-6">
            Couldn&rsquo;t load submissions. See the error above and try again.
          </p>
        )}
        {!loadingSubs && !subsLoadError && visibleSubmissions.length === 0 && (
          <p className="text-sm text-slate-400 text-center py-6">No submissions found.</p>
        )}

        {!loadingSubs && visibleSubmissions.length > 0 && (
          <div className="space-y-1.5">
            <div className="flex items-center gap-1.5 text-[11px] text-slate-500 mb-2">
              <Info className="w-3 h-3" />
              <span>Top = most recent, bottom = oldest. Drag the handle or use the arrows to reorder.</span>
            </div>
            {visibleSubmissions.map((sub, idx) => {
              // A2F-1024 — two independent hidden states:
              //  • isDisabled — sticky user intent ("keep out"); reversible via Enable.
              //  • deletedAt  — ingest tombstone (source folder gone); auto-returns
              //    if the folder reappears, so it has no enable/disable — just its badge.
              const isRemoved = !!sub.deletedAt;
              const rowHidden = isHidden(sub);
              // Only active rows carry an active "Submission N" number; hidden
              // rows come back with display_sequence=0 (backend, A2F-1024), so we
              // show their folder name instead of "Submission 0".
              const folderLabel = sub.submissionType || sub.notes || `Seq ${sub.sequenceNumber}`;
              // Reorder operates on the visible rows. In the default view those
              // are all active, so every visible row is freely draggable. With
              // Show-hidden ON, hidden rows are shown but kept un-draggable
              // (they're outside the active sequence); the stable merge in
              // handleSaveOrder preserves them, so Save works in either state.
              const canReorder = canContribute && !rowHidden;
              const togglingThis = togglingDisableSubId === sub.id;
              return (
              <div
                key={sub.id}
                data-testid="submission-row"
                data-submission-id={sub.id}
                data-hidden={rowHidden ? 'true' : 'false'}
                draggable={canReorder}
                onDragStart={() => canReorder && handleSubDragStart(idx)}
                onDragOver={(e) => canReorder && handleSubDragOver(e, idx)}
                onDrop={() => canReorder && handleSubDrop(idx)}
                onDragEnd={handleSubDragEnd}
                className={`group flex items-center gap-3 px-3 py-2.5 rounded-lg border transition-colors ${
                  dragOverIdx === idx
                    ? 'border-brand-orange bg-orange-50/40'
                    : 'border-slate-200 hover:border-slate-300'
                }${rowHidden ? ' bg-slate-50/60' : ''}`}
              >
                <div
                  className={`p-1 rounded ${canReorder ? 'cursor-grab active:cursor-grabbing text-slate-400 hover:text-brand-orange hover:bg-orange-50' : 'text-slate-300 cursor-not-allowed'}`}
                  title={
                    !canContribute ? 'Requires Contributor role'
                    : rowHidden ? 'Hidden submissions are not part of the active order'
                    : 'Drag to reorder'
                  }
                >
                  <GripVertical className="w-5 h-5 shrink-0" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    {/* Active rows show "Submission N"; hidden rows have no active
                        number (display_sequence=0), so we label them by folder. */}
                    <p className={`text-sm font-medium truncate ${rowHidden ? 'text-slate-500' : 'text-brand-charcoal'}`}>
                      {rowHidden ? folderLabel : `Submission ${sub.displaySequence}`}
                    </p>
                    {/* State badges — only ever visible in the Show-hidden view. */}
                    {sub.isDisabled && (
                      <span
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-slate-200 text-slate-600 shrink-0"
                        title="You disabled this submission. Ingest won't reprocess it. Click Enable to restore."
                      >
                        <Ban className="w-3 h-3" />
                        Disabled
                      </span>
                    )}
                    {isRemoved && (
                      <span
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-amber-100 text-amber-700 shrink-0"
                        title="The source folder is no longer visible to ingest. This returns automatically if the folder reappears."
                      >
                        <TriangleAlert className="w-3 h-3" />
                        Removed from source
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-slate-400 truncate">
                    {/* Active rows lead the subline with the folder name (the
                        "Submission N" number is above). Hidden rows already show
                        the folder name above, so theirs starts with the date. */}
                    {!rowHidden && `${folderLabel} \u2022 `}
                    {sub.submissionDate ? `${sub.submissionDate} \u2022 ` : ''}
                    {sub.documentCount} doc{sub.documentCount !== 1 ? 's' : ''}
                  </p>
                </div>
                <div className="flex items-center gap-0.5 shrink-0 opacity-60 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={(e) => { e.stopPropagation(); handleMoveSub(idx, 'up'); }}
                    disabled={!canReorder || idx === 0}
                    title={canReorder ? 'Move up (more recent)' : !canContribute ? 'Requires Contributor role' : 'Reorder is unavailable here'}
                    className="p-1 text-slate-400 hover:text-brand-orange hover:bg-orange-50 rounded disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-slate-400"
                  >
                    <ChevronUp className="w-4 h-4" />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleMoveSub(idx, 'down'); }}
                    disabled={!canReorder || idx === visibleSubmissions.length - 1}
                    title={canReorder ? 'Move down (older)' : !canContribute ? 'Requires Contributor role' : 'Reorder is unavailable here'}
                    className="p-1 text-slate-400 hover:text-brand-orange hover:bg-orange-50 rounded disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-slate-400"
                  >
                    <ChevronDown className="w-4 h-4" />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleRescanSubmission(sub.id); }}
                    disabled={!canContribute || rescanTaskState.isScanning}
                    title={
                      !canContribute
                        ? 'Requires Contributor role'
                        : rescanTaskState.isScanning && rescanningSubId === sub.id
                          ? 'Rescanning…'
                          : rescanTaskState.isScanning
                            ? 'Another rescan is running'
                            : 'Rescan this submission to pick up file changes'
                    }
                    className="p-1 text-slate-400 hover:text-brand-orange hover:bg-orange-50 rounded disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-slate-400"
                  >
                    {rescanTaskState.isScanning && rescanningSubId === sub.id ? (
                      <LoaderCircle className="w-4 h-4 animate-spin" />
                    ) : (
                      <RefreshCw className="w-4 h-4" />
                    )}
                  </button>
                </div>
                {/* A2F-1024 — the ONLY per-row lifecycle action is the soft,
                    reversible enable/disable toggle. Submissions are never
                    hard-deleted (no kebab, no "Delete permanently"). A
                    removed-from-source row (deleted_at) has no enable/disable —
                    it auto-returns when its folder reappears — so it shows just
                    its badge (above) and no action here. */}
                {!isRemoved && (
                  <div className="flex items-center gap-0.5 shrink-0" onClick={(e) => e.stopPropagation()}>
                    <button
                      data-testid="submission-toggle-disable"
                      onClick={(e) => { e.stopPropagation(); handleToggleDisable(sub.id, !sub.isDisabled); }}
                      disabled={!canEditProject || togglingThis}
                      title={
                        !canEditProject
                          ? 'Requires Lead role'
                          : sub.isDisabled
                            ? 'Enable — include this submission again; ingest will reprocess its folder'
                            : 'Disable — keep this folder out of the project (reversible, files untouched)'
                      }
                      className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium border transition-colors shrink-0 ${
                        sub.isDisabled
                          ? 'border-emerald-300 text-emerald-700 hover:bg-emerald-50'
                          : 'border-slate-200 text-slate-500 hover:text-slate-700 hover:bg-slate-50'
                      }${(!canEditProject || togglingThis) ? ' opacity-50 cursor-not-allowed' : ''}`}
                    >
                      {togglingThis ? (
                        <LoaderCircle className="w-3.5 h-3.5 animate-spin" />
                      ) : sub.isDisabled ? (
                        <RotateCcw className="w-3.5 h-3.5" />
                      ) : (
                        <Ban className="w-3.5 h-3.5" />
                      )}
                      {sub.isDisabled ? 'Enable' : 'Disable'}
                    </button>
                  </div>
                )}
              </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Ingest progress (stays visible on error for retry/dismiss) */}
      {(ingestTaskState.isScanning || ingestTaskState.error) && (
        <div className="border border-slate-200 rounded-lg bg-white">
          <ScanProgress
            state={ingestTaskState}
            onCancel={ingestTaskState.error ? resetIngest : cancelIngest}
            onRetry={ingestTaskState.error ? handleReIngest : undefined}
            featureLabel="submissions"
            itemLabel="Submission"
          />
        </div>
      )}

      {/* A2F-757 — per-submission rescan progress */}
      {rescanningSubId && (rescanTaskState.isScanning || rescanTaskState.error) && (
        <div className="border border-slate-200 rounded-lg bg-white">
          <ScanProgress
            state={rescanTaskState}
            onCancel={
              rescanTaskState.error
                ? () => setRescanningSubId(null)
                : () => { cancelRescan(); setRescanningSubId(null); }
            }
            onRetry={
              rescanTaskState.error
                ? () => handleRescanSubmission(rescanningSubId)
                : undefined
            }
            featureLabel="rescan"
            itemLabel="Submission"
          />
        </div>
      )}

      {/* Partial ingest warning */}
      {activeProject.lastIngestStatus === 'partial' && !ingestTaskState.isScanning && (
        <div className="px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
          <TriangleAlert className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
          <p className="text-sm text-amber-800">
            The last ingestion was incomplete. Some submission data may be missing. Click Re-Ingest to rescan.
          </p>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-3">
        <button
          onClick={handleReIngest}
          disabled={ingestTaskState.isScanning || !canContribute}
          title={!canContribute ? 'Requires Contributor role' : undefined}
          className={`flex items-center gap-2 px-4 py-2 border border-orange-300 text-brand-orange rounded-lg text-sm font-medium hover:bg-orange-50 transition-colors disabled:opacity-50${!canContribute ? ' cursor-not-allowed' : ''}`}
        >
          <RefreshCw className={`w-4 h-4 ${ingestTaskState.isScanning ? 'animate-spin' : ''}`} />
          {ingestTaskState.isScanning ? 'Re-Ingesting...' : 'Re-Ingest'}
        </button>

        {!confirmDelete ? (
          <button
            onClick={() => setConfirmDelete(true)}
            disabled={!canDeleteProjects}
            title={!canDeleteProjects ? 'Requires Lead role' : undefined}
            className={`flex items-center gap-2 px-4 py-2 border border-red-300 text-red-600 rounded-lg text-sm font-medium hover:bg-red-50 transition-colors${!canDeleteProjects ? ' opacity-50 cursor-not-allowed' : ''}`}
          >
            <Trash2 className="w-4 h-4" />
            Delete Project
          </button>
        ) : (
          <div className="flex items-center gap-2">
            <span className="text-sm text-red-600">Are you sure?</span>
            <button
              onClick={handleDelete}
              disabled={deleting}
              className="px-3 py-1.5 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors disabled:opacity-50"
            >
              {deleting ? 'Deleting...' : 'Yes, Delete'}
            </button>
            <button
              onClick={() => setConfirmDelete(false)}
              className="px-3 py-1.5 border border-slate-200 text-slate-600 rounded-lg text-sm font-medium hover:bg-slate-50 transition-colors"
            >
              Cancel
            </button>
          </div>
        )}
      </div>

      {/* Project Switcher (if multiple projects) */}
      {projects.length > 1 && (
        <div className="space-y-3">
          <h2 className="text-base font-semibold text-brand-charcoal">All Projects</h2>
          <div className="space-y-2">
            {projects.map((p) => (
              <div
                key={p.id}
                className={`bg-white rounded-xl border p-4 transition-colors ${
                  p.id === activeProject.id
                    ? 'border-brand-orange bg-orange-50/30'
                    : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <div className="flex items-center justify-between">
                  <button
                    onClick={() => setActiveProject(p)}
                    className="flex-1 text-left min-w-0"
                  >
                    <p className="text-sm font-medium text-brand-charcoal">{p.name}</p>
                    <p className="text-xs text-slate-500 mt-0.5">IND {p.indNumber}</p>
                  </button>
                  <div className="flex items-center gap-3 shrink-0">
                    <div className="text-right">
                      <p className="text-xs text-slate-400">{p.submissionCount} submissions</p>
                      <p className="text-xs text-slate-400">{p.documentCount} documents</p>
                    </div>
                    {confirmDeleteListId === p.id ? (
                      <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={() => handleDeleteFromList(p.id)}
                          disabled={deletingListId === p.id}
                          className="px-2.5 py-1 bg-red-600 text-white rounded-lg text-xs font-medium hover:bg-red-700 transition-colors disabled:opacity-50"
                        >
                          {deletingListId === p.id ? 'Deleting...' : 'Confirm'}
                        </button>
                        <button
                          onClick={() => setConfirmDeleteListId(null)}
                          className="px-2.5 py-1 border border-slate-200 text-slate-600 rounded-lg text-xs font-medium hover:bg-slate-50 transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={(e) => { e.stopPropagation(); setConfirmDeleteListId(p.id); }}
                        disabled={!canDeleteProjects}
                        className={`p-1.5 text-slate-300 hover:text-red-500 rounded-lg hover:bg-red-50 transition-colors${!canDeleteProjects ? ' opacity-50 cursor-not-allowed' : ''}`}
                        title={!canDeleteProjects ? 'Requires Lead role' : 'Delete project'}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

